import React, { useCallback, useRef, useState } from "react";
import { Container, Button } from "reactstrap";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSliders } from "@fortawesome/free-solid-svg-icons";
import {
  AspectRatioId,
  ASPECT_RATIOS,
  MediaSourceId,
  WorkflowId,
} from "../../cores/videoGen/types";
import { WorkflowSelector } from "../component/videoGen/WorkflowSelector";
import { AspectRatioSelector } from "../component/videoGen/AspectRatioSelector";
import { PromptInput } from "../component/videoGen/PromptInput";
import { MediaSourceCards } from "../component/videoGen/MediaSourceCards";
import {
  ToolsPopover,
  ToolsConfig,
  DEFAULT_TOOLS,
} from "../component/videoGen/ToolsPopover";
import Layout from "../layout/Layout";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import "../styles/VideoGen.scss";

type View = "workflow" | "create";

export default function VideoGen() {
  const intl = useIntl();
  const toolsTargetRef = useRef<HTMLButtonElement>(null);

  const [view, setView] = useState<View>("workflow");
  const [workflow, setWorkflow] = useState<WorkflowId | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioId>("portrait");
  const [prompt, setPrompt] = useState("");
  const [voice, setVoice] = useState("Matilda");
  const [mediaSource, setMediaSource] = useState<MediaSourceId>("stock");
  const [toolsConfig, setToolsConfig] = useState<ToolsConfig>(DEFAULT_TOOLS);
  const [toolsPopoverOpen, setToolsPopoverOpen] = useState(false);

  const handleWorkflowSelect = useCallback((id: WorkflowId) => {
    if (id === "feedback") return;
    setWorkflow(id);
    setView(id === "ideaToVideo" ? "create" : "workflow");
  }, []);

  const handleChangeWorkflow = useCallback(() => {
    setView("workflow");
    setWorkflow(null);
  }, []);

  const handleGenerate = useCallback(() => {
    // TODO: llamar API POST /v2/prompt-to-video cuando exista backend
    const payload = {
      prompt,
      voice,
      aspectRatio: ASPECT_RATIOS[aspectRatio],
      useGetty: mediaSource === "istock" || toolsConfig.istock,
      useGenerativeImage: mediaSource === "aiImage" || toolsConfig.aiImage,
      minDimensionPixels: 1080,
    };
    console.log("VideoGen payload (front only):", payload);
  }, [prompt, voice, aspectRatio, mediaSource, toolsConfig]);

  const aspectRatioLabel =
    aspectRatio === "portrait"
      ? "Retrato (9:16)"
      : aspectRatio === "landscape"
        ? "Paisaje (16:9)"
        : "Cuadrado (1:1)";

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.sidebar.ia.videoGen" })}
      />
      <Content className="video-gen-content">
        <Container className="video-gen-screen py-4">
          {view === "workflow" ? (
            <WorkflowSelector onSelect={handleWorkflowSelect} />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                  color="primary"
                  className="video-gen-screen__btn-new"
                  onClick={() => setView("workflow")}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  {intl.formatMessage({ id: "app.videoGen.newVideo" })}
                </Button>
                <Button color="outline-secondary" size="sm" disabled>
                  <FontAwesomeIcon icon={faSliders} className="mr-2" />
                  {intl.formatMessage({ id: "app.videoGen.preset" })}
                </Button>
              </div>

              <AspectRatioSelector value={aspectRatio} onChange={setAspectRatio} />

              <div className="video-gen-screen__form mt-4">
                <PromptInput
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  voice={voice}
                  onVoiceChange={setVoice}
                  languageLabel={intl.formatMessage({ id: "app.videoGen.auto" })}
                  onToolsClick={() => setToolsPopoverOpen((v) => !v)}
                  toolsButtonRef={toolsTargetRef}
                  onMusicClick={() => {}}
                  onSearchClick={() => {}}
                />

            <ToolsPopover
              id="video-gen-tools"
              target={toolsTargetRef}
              isOpen={toolsPopoverOpen}
                  toggle={() => setToolsPopoverOpen((v) => !v)}
                  config={toolsConfig}
                  onChange={setToolsConfig}
                />

                <MediaSourceCards value={mediaSource} onChange={setMediaSource} />

                <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                  <Button
                    color="link"
                    className="p-0"
                    onClick={handleChangeWorkflow}
                  >
                    {intl.formatMessage({ id: "app.videoGen.changeWorkflow" })}
                  </Button>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={!prompt.trim()}
                  >
                    {intl.formatMessage({ id: "app.videoGen.generateScript" })}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Container>
      </Content>
    </Layout>
  );
}
